import type { ApiAgeGenderBucket, HouseholdInsightsApiResponse } from '../apiTypes.ts';
import type { AgeGenderData } from '../chartTypes.ts';
import { AGE_BUCKETS, GENDER } from '../constants.ts';
import { toCount, toPercent } from './utils.ts';

const EMPTY_AGE_GENDER: AgeGenderData = {
  rows: [],
  maleShare: undefined,
  femaleShare: undefined,
  unknownGender: undefined,
  unknownAge: undefined,
};

const isMale = ({ gender }: ApiAgeGenderBucket) => gender === GENDER.male;
const isFemale = ({ gender }: ApiAgeGenderBucket) => gender === GENDER.female;

export const parseAgeGender = (response: HouseholdInsightsApiResponse | undefined): AgeGenderData => {
  const buckets = response?.age_gender ?? [];
  const total = buckets.reduce((sum, { count }) => sum + toCount(count), 0);

  if (total === 0) {
    return EMPTY_AGE_GENDER;
  }

  const countOf = (predicate: (bucket: ApiAgeGenderBucket) => boolean) =>
    buckets.filter(predicate).reduce((sum, { count }) => sum + toCount(count), 0);
  const shareOf = (predicate: (bucket: ApiAgeGenderBucket) => boolean) => toPercent(countOf(predicate), total);

  const maleCount = countOf(isMale);
  const femaleCount = countOf(isFemale);

  return {
    rows: AGE_BUCKETS.map(age => ({
      label: age,
      leftValue: shareOf(bucket => bucket.age_bucket === age && isMale(bucket)),
      rightValue: shareOf(bucket => bucket.age_bucket === age && isFemale(bucket)),
    })),
    // Legend split is relative to users with a known gender — computed from raw counts so rounding can't skew it
    maleShare: toPercent(maleCount, maleCount + femaleCount),
    femaleShare: toPercent(femaleCount, maleCount + femaleCount),
    unknownGender: shareOf(bucket => !isMale(bucket) && !isFemale(bucket)),
    unknownAge: shareOf(bucket => !AGE_BUCKETS.includes(bucket.age_bucket)),
  };
};
